import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DraftDTO, ScheduledMessageDTO } from "@justone/shared";
import { http } from "./http";

export const composeKeys = {
  drafts: ["drafts"] as const,
  scheduled: ["scheduled"] as const,
};

/** 쓰다 만 글 (모든 기기 공통) */
export const useDrafts = () => useQuery({ queryKey: composeKeys.drafts, queryFn: () => http.get<DraftDTO[]>("/api/drafts"), staleTime: 30_000 });

/** 예약한 메시지 (보내기 전, 보내지 못한 것) */
export const useScheduled = () => useQuery({ queryKey: composeKeys.scheduled, queryFn: () => http.get<ScheduledMessageDTO[]>("/api/scheduled"), staleTime: 30_000 });

export type DraftPlace = { channelId?: number; conversationId?: number; parentId?: number };

/** 초안 저장 (빈 글이면 서버가 지운다). 목록 캐시도 바로 고친다 */
export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DraftPlace & { body: string }) => http.put<DraftDTO | null>("/api/drafts", body),
    onSuccess: (d, v) =>
      qc.setQueryData<DraftDTO[]>(composeKeys.drafts, (list) => {
        if (!list) return list;
        const key = `${v.channelId ? `c:${v.channelId}` : `d:${v.conversationId}`}${v.parentId ? `:t:${v.parentId}` : ""}`;
        const rest = list.filter((x) => x.key !== key);
        return d ? [d, ...rest] : rest;
      }),
  });
}

export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.delete(`/api/drafts/${id}`),
    onSuccess: (_r, id) => qc.setQueryData<DraftDTO[]>(composeKeys.drafts, (list) => list?.filter((d) => d.id !== id)),
  });
}

export type ScheduleBody = DraftPlace & { body: string; attachmentIds: number[]; alsoInChannel?: boolean; sendAt: string };

export function useScheduleMutations() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: composeKeys.scheduled });
  return {
    create: useMutation({ mutationFn: (body: ScheduleBody) => http.post<ScheduledMessageDTO>("/api/scheduled", body), onSuccess: refresh }),
    update: useMutation({ mutationFn: ({ id, ...body }: { id: number; sendAt?: string; body?: string }) => http.patch<ScheduledMessageDTO>(`/api/scheduled/${id}`, body), onSuccess: refresh }),
    sendNow: useMutation({ mutationFn: (id: number) => http.post<{ ok: true; path: string }>(`/api/scheduled/${id}/send`), onSettled: refresh }),
    cancel: useMutation({ mutationFn: (id: number) => http.delete(`/api/scheduled/${id}`), onSuccess: refresh }),
  };
}
